/**
 * Sparrow Supply - AJAX Filtering System
 * Modern, instant filtering without page reloads
 */

(function() {
  'use strict';

  const SF_FILTERS = {
    container: null,
    form: null,
    sidebar: null,
    isLoading: false,
    debounceTimer: null,
    controller: null,

    init() {
      this.container = document.querySelector('.sf-collection');
      if (!this.container) return;

      this.form = document.getElementById('sfFilterForm');
      this.sidebar = document.getElementById('sfFilters');

      if (!this.form) return;

      this.bindEvents();
      window.addEventListener('popstate', () => this.fetchAndRender(window.location.href, false));
    },

    bindEvents() {
      // Filter checkboxes
      this.form.querySelectorAll('input[type="checkbox"]').forEach(cb => {
        cb.addEventListener('change', () => this.submitFilters());
      });

      // Price inputs - debounced
      this.form.querySelectorAll('.sf-price-field input').forEach(input => {
        input.addEventListener('input', () => {
          clearTimeout(this.debounceTimer);
          this.debounceTimer = setTimeout(() => this.submitFilters(), 600);
        });
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.submitFilters();
          }
        });
      });

      // Price apply button
      const priceBtn = this.form.querySelector('.sf-price-apply');
      if (priceBtn) {
        priceBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.submitFilters();
        });
      }

      // Prevent default form submit
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.submitFilters();
      });

      // Sort dropdown - needs special handling
      const sortSelect = document.getElementById('sfSort');
      if (sortSelect) {
        sortSelect.addEventListener('change', () => {
          // Extract just the sort_by value and add to current filters
          const selectedOption = sortSelect.options[sortSelect.selectedIndex];
          const optionUrl = new URL(selectedOption.value, window.location.origin);
          const sortBy = optionUrl.searchParams.get('sort_by');

          if (sortBy) {
            const currentUrl = new URL(this.buildCurrentUrl());
            currentUrl.searchParams.set('sort_by', sortBy);
            this.fetchAndRender(currentUrl.toString());
          }
        });
      }

      // Active filter removal chips
      this.container.addEventListener('click', (e) => {
        const chip = e.target.closest('.sf-active-filter');
        if (chip) {
          e.preventDefault();
          this.fetchAndRender(chip.href);
        }
      });

      // Clear all filters link
      const clearLink = this.form.querySelector('.sf-filter-clear');
      if (clearLink) {
        clearLink.addEventListener('click', (e) => {
          e.preventDefault();
          this.fetchAndRender(clearLink.href);
        });
      }

      // Pagination
      this.container.addEventListener('click', (e) => {
        const pageLink = e.target.closest('.sf-page-prev, .sf-page-next');
        if (pageLink) {
          e.preventDefault();
          this.fetchAndRender(pageLink.href);
          window.scrollTo({ top: this.container.offsetTop - 20, behavior: 'smooth' });
        }
      });
    },

    buildCurrentUrl() {
      // Build URL from current form state
      const formData = new FormData(this.form);
      const url = new URL(window.location.pathname, window.location.origin);

      // Add all form fields
      for (const [key, value] of formData.entries()) {
        if (value && value.toString().trim() !== '') {
          url.searchParams.append(key, value);
        }
      }

      // Preserve current sort if any
      const currentSort = new URL(window.location.href).searchParams.get('sort_by');
      if (currentSort) {
        url.searchParams.set('sort_by', currentSort);
      }

      return url.toString();
    },

    submitFilters() {
      const url = this.buildCurrentUrl();
      this.fetchAndRender(url);
    },

    async fetchAndRender(url, pushState = true) {
      // Cancel any in-flight request
      if (this.controller) {
        this.controller.abort();
      }
      this.controller = new AbortController();

      this.showLoading();

      try {
        // Get section ID from the wrapper
        const sectionWrapper = this.container.closest('[id^="shopify-section-"]');
        const sectionId = sectionWrapper ? sectionWrapper.id.replace('shopify-section-', '') : 'main';

        // Build fetch URL with section parameter
        const fetchUrl = new URL(url, window.location.origin);
        fetchUrl.searchParams.set('sections', sectionId);

        const response = await fetch(fetchUrl.toString(), {
          signal: this.controller.signal,
          headers: { 'Accept': 'application/json' }
        });

        if (!response.ok) throw new Error('Fetch failed');

        const json = await response.json();
        const newHtml = json[sectionId];

        if (!newHtml) throw new Error('No section HTML returned');

        // Parse and update
        const parser = new DOMParser();
        const doc = parser.parseFromString(newHtml, 'text/html');
        const newSection = doc.querySelector('.sf-collection');

        if (!newSection) throw new Error('Could not parse new content');

        // Update main content
        const currentMain = this.container.querySelector('.sf-coll-main');
        const newMain = newSection.querySelector('.sf-coll-main');
        if (currentMain && newMain) {
          currentMain.innerHTML = newMain.innerHTML;
        }

        // Update header count
        const currentCount = this.container.querySelector('.sf-coll-count');
        const newCount = newSection.querySelector('.sf-coll-count');
        if (currentCount && newCount) {
          currentCount.textContent = newCount.textContent;
        }

        // Update sidebar (preserve open states)
        const openFilters = new Set();
        this.sidebar.querySelectorAll('.sf-filter-group[open]').forEach(g => {
          const label = g.querySelector('.sf-filter-title');
          if (label) openFilters.add(label.textContent.trim());
        });

        const newSidebar = newSection.querySelector('.sf-coll-sidebar');
        if (newSidebar) {
          this.sidebar.innerHTML = newSidebar.innerHTML;

          // Restore open states
          this.sidebar.querySelectorAll('.sf-filter-group').forEach(g => {
            const label = g.querySelector('.sf-filter-title');
            if (label && openFilters.has(label.textContent.trim())) {
              g.setAttribute('open', '');
            }
          });

          // Rebind events on new form
          this.form = document.getElementById('sfFilterForm');
          this.bindEvents();
        }

        // Close mobile sidebar
        this.sidebar.classList.remove('is-open');

        // Update browser URL
        if (pushState) {
          const cleanUrl = new URL(url, window.location.origin);
          cleanUrl.searchParams.delete('sections');
          history.pushState({}, '', cleanUrl.toString());
        }

      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('AJAX filter error:', err);
        // Fallback to full page load
        window.location.href = url;
      } finally {
        this.hideLoading();
        this.controller = null;
      }
    },

    showLoading() {
      this.container.classList.add('sf-loading');
      const grid = this.container.querySelector('.sf-products');
      if (grid && !grid.querySelector('.sf-loading-overlay')) {
        grid.insertAdjacentHTML('beforeend', '<div class="sf-loading-overlay"><div class="sf-loading-spinner"></div></div>');
      }
    },

    hideLoading() {
      this.container.classList.remove('sf-loading');
      const overlay = this.container.querySelector('.sf-loading-overlay');
      if (overlay) overlay.remove();
    }
  };

  // Init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => SF_FILTERS.init());
  } else {
    SF_FILTERS.init();
  }
})();
